import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, DataTable, IdCell, PageHead, SearchBox, StatCard, Tabs, dateStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { orderApi } from '../api';

const TABS = [
  { value: '', label: 'All' },
  { value: 'placed', label: 'Awaiting approval' },
  { value: 'accepted,packed', label: 'Processing' },
  { value: 'dispatched,in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled,rejected', label: 'Closed' },
];

const Orders = () => {
  const navigate = useNavigate();
  const list = useList(orderApi.list, { status: '', search: '', paymentStatus: '' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    orderApi.stats().then(res => setStats(res.data)).catch(() => {});
  }, []);

  const columns = [
    {
      key: 'orderNumber',
      label: 'Order',
      render: row => (
        <>
          <div className="cell-strong mono">{row.orderNumber}</div>
          <div className="cell-muted">{dateStr(row.createdAt)}</div>
        </>
      ),
    },
    { key: 'buyer', label: 'Buyer', render: row => <IdCell image={row.buyer?.logo} name={row.buyer?.name} sub={row.buyer?.code} size="sm" /> },
    { key: 'seller', label: 'Seller', render: row => <IdCell image={row.seller?.logo} name={row.seller?.name} sub={row.seller?.code} size="sm" /> },
    { key: 'items', label: 'Items', align: 'center', render: row => num(row.items?.length || 0) },
    {
      key: 'grandTotal',
      label: 'Value',
      align: 'right',
      render: row => (
        <>
          <div className="cell-strong">{inr(row.grandTotal)}</div>
          <div className="cell-muted">GST {inr(row.totalGst)}</div>
        </>
      ),
    },
    { key: 'payment', label: 'Payment', render: row => <Badge status={row.payment?.status} /> },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead title="Orders" subtitle="Every order flowing through the network, end to end." />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Lifetime revenue" value={inr(stats.lifetimeRevenue, true)} icon="₹" foot={`${num(stats.deliveredOrders)} delivered`} />
          <StatCard label="This month" value={inr(stats.monthRevenue, true)} icon="📈" foot={`${num(stats.monthOrders)} orders`} />
          <StatCard label="Today" value={num(stats.todayOrders)} icon="🛒" />
          <StatCard label="Awaiting approval" value={num(stats.pendingApproval)} icon="⏳" onClick={() => list.setFilter('status', 'placed')} />
          <StatCard label="In transit" value={num(stats.byStatus.dispatched + stats.byStatus.in_transit)} icon="🚚" />
          <StatCard label="Total orders" value={num(stats.totalOrders)} icon="◈" />
        </div>
      )}

      <Tabs tabs={TABS} value={list.filters.status} onChange={v => list.setFilter('status', v)} />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search order number…" />
        <select className="select" style={{ width: 180 }} value={list.filters.paymentStatus} onChange={e => list.setFilter('paymentStatus', e.target.value)}>
          <option value="">Any payment status</option>
          <option value="pending">Unpaid</option>
          <option value="partial">Partially paid</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
        </select>
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} orders` : ''}</span>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={row => navigate(`/admin/orders/${row._id}`)}
      />
    </>
  );
};

export default Orders;
